import axios from 'axios';
import { redirectToLogin } from 'utils/auth';

// Base URL configuration. The value must be provided via the environment.
const BASE_URL = process.env.REACT_APP_API_URL;
if (!BASE_URL) {
  throw new Error('REACT_APP_API_URL is not set');
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

/**
 * Request interceptor.
 * Attaches the authentication header to every request.
 */
apiClient.interceptors.request.use(
  (config) => {
    config.headers = {
      ...config.headers,
      ...getAuthHeaders(),
    };
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const STATUS_MESSAGES = {
  400: 'There was a problem with the request',
  401: 'Authentication failed. Please sign in again',
  403: 'You do not have permission to perform this action',
  404: 'The requested resource was not found',
  409: 'A data conflict occurred. Please refresh the page',
  500: 'An internal server error occurred',
  502: 'Failed to communicate with the server',
  503: 'The server is temporarily unavailable',
};

/**
 * Extract an error message from the server response.
 */
const extractErrorMessage = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;

  if (data?.detail) {
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    if (Array.isArray(data.detail)) {
      const fields = data.detail.map((d) => {
        const field = (d.loc || []).filter((l) => l !== 'body').join('.');
        return field ? `${field}: ${d.msg}` : d.msg;
      });
      return `Invalid input (${fields.join(', ')})`;
    }
  }

  if (status && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  if (!error.response) {
    return 'Network error: cannot connect to the server';
  }

  return 'An error occurred';
};

/**
 * Response interceptor.
 * Centralized error handling, logging, and user-facing messages.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const url = error.config?.url || '';
    const method = (error.config?.method || '').toUpperCase();
    const userMessage = extractErrorMessage(error);

    console.error(
      `[API Error] ${method} ${url} -> ${status || 'NETWORK_ERROR'}:`,
      error.response?.data || error.message
    );

    // A 401 outside the login endpoint is treated as an
    // expired session, so redirect to the login page.
    const isAuthEndpoint = url.includes('/api/auth/login');
    if (status === 401 && !isAuthEndpoint) {
      redirectToLogin();
    }

    const enhancedError = new Error(userMessage);
    enhancedError.status = status;
    enhancedError.serverDetail = error.response?.data?.detail;
    enhancedError.originalError = error;
    return Promise.reject(enhancedError);
  }
);

export const get = async (endpoint, paramsOrConfig = {}) => {
  const { responseType, signal, headers: extraHeaders, timeout, ...params } = paramsOrConfig;
  const config = { params };
  if (responseType) config.responseType = responseType;
  if (signal) config.signal = signal;
  if (extraHeaders) config.headers = extraHeaders;
  if (timeout) config.timeout = timeout;

  const response = await apiClient.get(endpoint, config);
  return response.data;
};

export const post = async (endpoint, data = {}, config = {}) => {
  const response = await apiClient.post(endpoint, data, config);
  return response.data;
};

export const put = async (endpoint, data = {}, config = {}) => {
  const response = await apiClient.put(endpoint, data, config);
  return response.data;
};

export const del = async (endpoint, config = {}) => {
  const response = await apiClient.delete(endpoint, config);
  return response.data;
};

export default apiClient;
